import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { HOTEL } from "../../data/content";

const LINKS = [
  { label: "Story", href: "#story" },
  { label: "Stays", href: "#stays" },
  { label: "Experiences", href: "#experiences" },
  { label: "Gallery", href: "#gallery" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTop = (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-sand-100/85 backdrop-blur-xl border-b border-forest-900/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <a
          href="#top"
          onClick={scrollTop}
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span
            className={`font-serif text-2xl sm:text-3xl leading-none tracking-tight transition-colors ${
              scrolled ? "text-forest-900" : "text-sand-100"
            }`}
          >
            Mecana
          </span>
          <span
            className={`text-[0.6rem] tracking-[0.3em] uppercase font-semibold mt-1 transition-colors ${
              scrolled ? "text-earth-600" : "text-sand-200/90"
            }`}
          >
            EcoHotel
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`text-sm font-medium tracking-wide transition-colors hover:text-earth-500 ${
                scrolled ? "text-forest-800" : "text-sand-100"
              }`}
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            data-testid="nav-book-button"
            className="inline-flex items-center gap-2 rounded-full bg-earth-500 hover:bg-earth-600 text-white px-5 py-2.5 text-sm font-semibold transition-colors duration-300"
          >
            Reserve
          </a>
        </nav>

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden p-2 rounded-full transition-colors ${
            scrolled ? "text-forest-900" : "text-sand-100"
          }`}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-sand-100/95 backdrop-blur-xl border-t border-forest-900/10 mt-3"
          >
            <div className="flex flex-col px-6 py-5 gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  className="text-forest-900 text-base font-medium py-1"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={`https://wa.me/${HOTEL.phoneDigits}`}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-whatsapp"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-forest-800 text-sand-100 px-5 py-3 text-sm font-semibold mt-1"
              >
                <MessageCircle size={18} /> WhatsApp
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
